import { useState, useCallback, useEffect, useRef } from 'react';
import { loadWebConfig } from '@kantera/config';
import { apiGet, ApiError } from '@kantera/api-client';
import { useAuth } from './useAuth';

const API_URL = loadWebConfig().apiUrl;

export interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /**
   * The API's machine `code` for a failed request, when its body carried one (ApiError.code), or
   * null. Two refusals can share a status and mean different things to show someone: a 403 because
   * the caller may not read something is not a 403 because a family has not consented (KAN-232).
   */
  errorCode?: string | null;
  /** True while the hook is automatically retrying a 503 response */
  retrying: boolean;
}

export interface UseApiOptions {
  skip?: boolean;
  dependencies?: unknown[];
}

/**
 * Generic API hook for fetching data with auth token.
 *
 * Wraps `@kantera/api-client`'s `apiGet` (KI-038): URL building, auth header
 * construction, 503 retry, and ApiError mapping live in the shared package.
 * This hook adds React-specific concerns: state management, AbortSignal-
 * driven cancellation on unmount/dependency-change, and `retrying` UI flag.
 *
 * Returns the full server response as `T`. All Kantera API endpoints return a
 * JSON envelope — callers should type `T` as the full envelope shape, e.g.:
 *   `useApi<{ data: Player[]; pagination: Pagination }>('/api/players')`
 *
 * @template T - Type of the full server response (envelope included)
 * @param endpoint - API endpoint (e.g., '/api/players', '/api/teams/123')
 * @param options - Optional configuration
 * @returns State object with data, loading, error, and refetch function
 */
export function useApi<T>(
  endpoint: string,
  options?: UseApiOptions,
): UseApiState<T> & { refetch: () => Promise<void> } {
  const { user, tenant, accessToken } = useAuth();
  const abortRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null,
    retrying: false,
  });

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request before starting a new one
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    if (!user || !tenant) {
      setState({
        data: null,
        loading: false,
        error: 'Not authenticated',
        retrying: false,
      });
      return;
    }

    try {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        errorCode: null,
        retrying: false,
      }));

      // org_id is derived from the JWT on the server — never sent by the client
      const result = await apiGet<T>(endpoint, {
        baseUrl: API_URL,
        token: accessToken,
        signal: controller.signal,
        retry503: true,
      });

      // Guard against state update after unmount (signal may not abort instantly)
      if (controller.signal.aborted) return;

      setState({
        data: result,
        loading: false,
        error: null,
        retrying: false,
      });
    } catch (err) {
      // Ignore abort errors — they are expected on cleanup
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const errorMessage =
        err instanceof ApiError
          ? `API error: ${err.status} ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      setState({
        data: null,
        loading: false,
        error: errorMessage,
        errorCode: err instanceof ApiError ? err.code : null,
        retrying: false,
      });
    }
  }, [user, tenant, accessToken, endpoint]);

  useEffect(() => {
    if (options?.skip === true) return;
    fetchData();

    return () => {
      // Abort in-flight request when effect re-runs or component unmounts
      abortRef.current?.abort();
    };
    // Spread caller dependencies so they compare by VALUE. Including the array
    // object itself made a fresh inline `dependencies: [x]` literal a new
    // reference every render, re-running this effect each render and aborting
    // the in-flight request before it resolved — an infinite fetch/abort loop
    // that left pages stuck on their loading skeleton (B5 /my-children). With
    // the spread, an inline array is safe and callers no longer need to memoize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, options?.skip, ...(options?.dependencies ?? [])]);

  return { ...state, refetch: fetchData };
}
